import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, X, Info } from 'lucide-react';
import { cn } from '../../utils/cn';

export type ToastType = 'success' | 'error' | 'info';

export interface ToastMessage {
  id: string;
  type: ToastType;
  message: string;
}

export const ToastContainer = () => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  useEffect(() => {
    const handleToast = (e: CustomEvent<ToastMessage>) => {
      setToasts((prev) => [...prev, e.detail]);
      // Auto-dismiss after 4 seconds
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== e.detail.id));
      }, 4000);
    };

    // Listen for custom event
    window.addEventListener('show-toast' as any, handleToast);
    return () => window.removeEventListener('show-toast' as any, handleToast);
  }, []);

  const removeToast = (id: string) => {
      setToasts(prev => prev.filter(t => t.id !== id));
  };

  return (
    <div className="fixed bottom-6 right-6 z-[100] flex flex-col gap-3 pointer-events-none">
      {toasts.map((toast) => (
        <div 
          key={toast.id} 
          className={cn(
            "pointer-events-auto min-w-[300px] max-w-md p-4 rounded-xl shadow-xl border flex items-start gap-3 animate-in slide-in-from-right-full duration-300",
            toast.type === 'success' ? "bg-white dark:bg-gray-800 border-l-4 border-l-emerald-500 text-gray-800 dark:text-gray-100 dark:border-gray-700" : 
            toast.type === 'error' ? "bg-white dark:bg-gray-800 border-l-4 border-l-red-500 text-gray-800 dark:text-gray-100 dark:border-gray-700" :
            "bg-white dark:bg-gray-800 border-l-4 border-l-blue-500 text-gray-800 dark:text-gray-100 dark:border-gray-700"
          )}
        >
          <div className={cn("mt-0.5 shrink-0", 
            toast.type === 'success' ? "text-emerald-500" : 
            toast.type === 'error' ? "text-red-500" : 
            "text-blue-500"
          )}>
             {toast.type === 'success' ? <CheckCircle size={22} className="fill-emerald-50 dark:fill-emerald-900/20" /> : 
              toast.type === 'error' ? <XCircle size={22} className="fill-red-50 dark:fill-red-900/20" /> :
              <Info size={22} className="fill-blue-50 dark:fill-blue-900/20" />
             }
          </div>
          <div className="flex-1">
             <h4 className={cn("font-bold text-sm", 
               toast.type === 'success' ? "text-emerald-700 dark:text-emerald-400" : 
               toast.type === 'error' ? "text-red-700 dark:text-red-400" : 
               "text-blue-700 dark:text-blue-400"
             )}>
               {toast.type === 'success' ? 'Success' : toast.type === 'error' ? 'Action Failed' : 'Information'}
             </h4>
             <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mt-0.5 leading-snug">{toast.message}</p>
          </div>
          <button onClick={() => removeToast(toast.id)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
             <X size={16} />
          </button>
        </div>
      ))}
    </div>
  );
};

export const showToast = (message: string, type: ToastType = 'success') => {
  const event = new CustomEvent('show-toast', {
    detail: { id: Date.now().toString(), type, message }
  });
  window.dispatchEvent(event);
};